// NarrationImagePanel.jsx
import React, { useRef, useState } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { toast } from 'react-toastify';
import { uploadCompositionImage } from './uploadHelper';

function ImageUploadField({ label, value, onChange, token, disabled }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be under 10MB.');
      return;
    }

    setUploading(true);
    try {
      const url = await uploadCompositionImage(file, token);
      if (!url) throw new Error('No URL returned');
      onChange(url);
      toast.success('Image uploaded');
    } catch (err) {
      console.error(err);
      toast.error('Upload failed. You can paste a URL instead.');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="narration-upload-field">
      {label && <label>{label}</label>}

      <div className="narration-upload-row">
        <input
          type="text"
          placeholder="https://… or upload below"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          disabled={disabled || uploading}
        />
        <button
          type="button"
          className="narration-upload-btn"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || uploading}
        >
          {uploading ? 'Uploading…' : 'Upload'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={handleFile}
        />
      </div>

      {value && (
        <div className="narration-image-preview narration-image-preview--small">
          <LazyLoadImage src={value} alt="" effect="blur" />
        </div>
      )}
    </div>
  );
}

export default function NarrationImagePanel({
  narrationImageMode,
  setNarrationImageMode,
  globalNarrationImage,
  setGlobalNarrationImage,
  segmentNarrationImages,
  setSegmentNarrationImages,
  currentSection,
  sectionName,
  userToken,
}) {
  const segments = (currentSection?.segment || []).filter(seg => {
    const text = (seg.text || '').trim();
    return !(text.startsWith('[CLIP:') && text.endsWith(']'));
  });

  const updateSegmentImage = (segmentIndex, url) => {
    setSegmentNarrationImages(prev => ({
      ...prev,
      [sectionName]: {
        ...(prev[sectionName] || {}),
        [segmentIndex]: url,
      },
    }));
  };

  return (
    <div className="narration-image-panel">
      <div className="narration-image-panel__header">
        <div>
          <h3>Narration Images</h3>
          <p>
            Override image pools for narration segments only. Clip segments are excluded.
            Upload an image or paste a URL.
          </p>
        </div>
      </div>

      <div className="narration-mode-toggle">
        <button
          type="button"
          className={`narration-mode-btn ${narrationImageMode === 'default' ? 'is-active' : ''}`}
          onClick={() => setNarrationImageMode('default')}
        >
          Normal image pool
        </button>
        <button
          type="button"
          className={`narration-mode-btn ${narrationImageMode === 'global' ? 'is-active' : ''}`}
          onClick={() => setNarrationImageMode('global')}
        >
          One global image
        </button>
        <button
          type="button"
          className={`narration-mode-btn ${narrationImageMode === 'per_segment' ? 'is-active' : ''}`}
          onClick={() => setNarrationImageMode('per_segment')}
        >
          Unique per segment
        </button>
      </div>

      {narrationImageMode === 'global' && (
        <div className="narration-global">
          <ImageUploadField
            label="Global narration image"
            value={globalNarrationImage}
            onChange={setGlobalNarrationImage}
            token={userToken}
          />
          <p className="teaser-studio__hint">
            When enabled, this image is used for every narration segment and overrides character image pools.
          </p>
        </div>
      )}

      {narrationImageMode === 'per_segment' && (
        <div className="narration-segment-list">
          {segments.length === 0 ? (
            <p className="teaser-studio__hint">No narration segments in this section.</p>
          ) : (
            segments.map(seg => {
              const realIndex = currentSection.segment.indexOf(seg);
              const value = segmentNarrationImages?.[sectionName]?.[realIndex] || '';

              return (
                <div key={realIndex} className="narration-segment-row">
                  <div className="narration-segment-row__meta">
                    <strong>{seg.character || 'Character'}</strong>
                    <span>
                      {(seg.text || '').slice(0, 80)}
                      {(seg.text || '').length > 80 ? '…' : ''}
                    </span>
                  </div>

                  <ImageUploadField
                    value={value}
                    onChange={url => updateSegmentImage(realIndex, url)}
                    token={userToken}
                  />
                </div>
              );
            })
          )}
        </div>
      )}

      {narrationImageMode === 'default' && (
        <div className="narration-global">
          <p className="teaser-studio__hint">
            Using your normal character image pools below. Switch mode to override with upload or URL.
          </p>
        </div>
      )}
    </div>
  );
}